// On-demand loader for the heavy vendor bundles.
//
// Highcharts (~575KB min) and d3 (~230KB) are each used by a handful of
// pages, but as static imports of application.js every visitor paid for
// both on every page. Each now builds as its own esbuild entrypoint
// (app/javascript/<name>_bundle.js) which populates a global and is fetched
// only when a controller actually needs it.
//
// Why script injection rather than esbuild's `--splitting`: splitting requires
// `--format=esm`, and propshaft digests the filenames it emits without
// rewriting the bare `import "./chunk-XYZ.js"` specifiers inside the bundle —
// so the chunks would 404 in production. Rails knows the digested URL, so it
// hands it over in a <meta> tag (see LazyBundlesHelper) and we inject a plain
// <script>. That also keeps the build on `--format=iife`.

// name -> Promise. Module scope survives Turbo navigations, so a bundle is
// fetched at most once per full page load.
const loads = new Map()

export function bundleUrl(name) {
  return document.querySelector(`meta[name="js-bundle-${name}"]`)?.content || null
}

// Resolves once <name>_bundle.js has executed (and therefore set its global).
// Rejects if the meta tag is missing or the script fails to load; a rejected
// load is not cached, so a later attempt can retry.
export function loadBundle(name) {
  if (loads.has(name)) return loads.get(name)

  const promise = new Promise((resolve, reject) => {
    const url = bundleUrl(name)
    if (!url) {
      reject(new Error(`No <meta name="js-bundle-${name}"> on the page — is the bundle registered in LazyBundlesHelper::LAZY_BUNDLES?`))
      return
    }

    // The <script> lives in <head>, which Turbo does not clear between
    // navigations, so on a Turbo visit it may already be there. Wait on it
    // rather than resolving blind — it might still be in flight.
    const existing = document.querySelector(`script[data-js-bundle="${name}"]`)
    if (existing) {
      if (existing.dataset.jsBundleReady) resolve()
      else existing.addEventListener("load", () => resolve(), { once: true })
      return
    }

    const script = document.createElement("script")
    script.src = url
    script.dataset.jsBundle = name
    script.addEventListener("load", () => {
      script.dataset.jsBundleReady = "true"
      resolve()
    }, { once: true })
    script.addEventListener("error", () => {
      script.remove()
      loads.delete(name)
      reject(new Error(`Failed to load the ${name} bundle from ${url}`))
    }, { once: true })
    document.head.appendChild(script)
  })

  promise.catch(() => {})
  loads.set(name, promise)
  return promise
}

// Test seam — the module-level cache would otherwise leak between examples.
export function resetLazyBundles() {
  loads.clear()
}
